/**
 * Request Store
 *
 * In-memory state for the requests feature plus local domain transitions
 * (create / update / submit / cancel). It deliberately has NO access to the
 * API or to the auth token: the `useRequests` hook orchestrates the network
 * calls and pushes the results back into this store via `setRequests` /
 * `updateOrAddRequest`. This keeps the store a pure state + state-machine
 * unit (SRP, Dependency Inversion) and avoids coupling two global stores.
 *
 * @module @features/requests/stores/request/requestStore
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { checkDevEnv } from '@shared/utils/env';

import type { RequestStore } from './requestTypes';
import { initialState, createRequestActions } from './requestActions';

/**
 * Zustand store for request state.
 */
export const useRequestStore = create<RequestStore>()(
    devtools(
        (set, get): RequestStore => ({
            ...initialState,
            ...createRequestActions(set, get),
        }),
        { enabled: checkDevEnv(), name: 'RequestStore' },
    ),
);
